import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../hooks/useData';
import { useVendorQuotes } from '../../../hooks/useVendorQuotes';
import { TABLES } from '../../../constants';
import { formatCurrency } from '../../../utils/formatters';
import { VAT_RATE } from '../../../utils/costCalculations';
import tableStyles from '../../../styles/tables.module.css';
import pageStyles from '../../../styles/pages.module.css';
import styles from './BudgetPage.module.css';

/**
 * ActiveBudgetTab - Shows selected quotes grouped by vendor type category
 *
 * Data source: vendor_quotes where status = 'selected'
 * Grouping: by vendor_type_category_enum (via vendor → vendor_type → category)
 * Each row = one selected quote with vendor name, trade, total, status
 * Category subtotals + grand total at bottom
 */
const ActiveBudgetTab = ({ currentProject }) => {
  const projectId = currentProject?.id;
  const navigate = useNavigate();

  // Fetch selected quotes via useVendorQuotes hook (reuse, don't duplicate)
  const { quotes, quoteItems, isLoading: quotesLoading } = useVendorQuotes(projectId);

  // Master vendors for names and vendor_type_id
  const { data: masterVendors, isLoading: vendorsLoading } = useData(TABLES.MASTER_VENDORS, {
    select: 'id, name, vendor_type_id',
    enabled: !!projectId,
  });

  // Vendor types for display names and categories
  const { data: vendorTypes, isLoading: typesLoading } = useData(TABLES.REFERENCE_VENDOR_TYPES, {
    enabled: !!projectId,
  });

  const isLoading = quotesLoading || vendorsLoading || typesLoading;

  // Vendor type name → quote page route.
  // Add new entries here as dedicated vendor type pages are created.
  const VENDOR_TYPE_ROUTES = {
    'קבלנות ראשית': '/boq/quotes',
    // 'חשמל': '/electrical/quotes',  // future example
  };
  const DEFAULT_QUOTE_ROUTE = '/vendors';

  // Lookup maps
  const vendorsMap = useMemo(() => {
    if (!masterVendors) return new Map();
    return new Map(masterVendors.map(v => [v.id, v]));
  }, [masterVendors]);

  const vendorTypesMap = useMemo(() => {
    if (!vendorTypes) return new Map();
    return new Map(vendorTypes.map(vt => [vt.id, vt]));
  }, [vendorTypes]);

  // Get total per quote — use vendor_quotes.total_price (canonical value set by vendor),
  // fall back to sum of line_price if total_price is missing
  const quoteTotals = useMemo(() => {
    if (!quotes) return new Map();
    const totals = new Map();
    quotes.forEach(q => {
      if (q.total_price != null) {
        totals.set(q.id, parseFloat(q.total_price));
      }
    });
    // Fill in any missing totals from line items
    if (quoteItems) {
      quoteItems.forEach(qi => {
        if (qi.line_price != null && !totals.has(qi.quote_id)) {
          const current = totals.get(qi.quote_id) || 0;
          totals.set(qi.quote_id, current + parseFloat(qi.line_price));
        }
      });
    }
    return totals;
  }, [quotes, quoteItems]);

  // Filter to selected quotes only, group by vendor type category
  const groupedQuotes = useMemo(() => {
    if (!quotes) return [];

    const selectedQuotes = quotes.filter(q => q.status === 'selected');
    const groups = {};
    const noCategoryKey = '__uncategorized__';

    selectedQuotes.forEach(q => {
      const vendor = vendorsMap.get(q.master_vendor_id);
      const vendorType = vendor?.vendor_type_id ? vendorTypesMap.get(vendor.vendor_type_id) : null;
      const categoryKey = vendorType?.category || noCategoryKey;
      const categoryName = vendorType?.category || 'לא מסווג';

      if (!groups[categoryKey]) {
        groups[categoryKey] = { categoryKey, categoryName, rows: [], subtotal: 0 };
      }

      const preVat = quoteTotals.get(q.id) || 0;
      const total = preVat * (1 + VAT_RATE);
      groups[categoryKey].rows.push({
        quote: q,
        vendorName: vendor?.name || 'ספק לא ידוע',
        tradeName: vendorType?.name || '—',
        total,
      });
      groups[categoryKey].subtotal += total;
    });

    // Sort groups alphabetically by category name
    return Object.values(groups).sort((a, b) => a.categoryName.localeCompare(b.categoryName, 'he'));
  }, [quotes, vendorsMap, vendorTypesMap, quoteTotals]);

  // Grand total
  const grandTotal = useMemo(
    () => groupedQuotes.reduce((sum, g) => sum + g.subtotal, 0),
    [groupedQuotes],
  );

  if (isLoading) {
    return (
      <div className={pageStyles.loadingContainer}>
        <div className={tableStyles.spinner} />
      </div>
    );
  }

  if (groupedQuotes.length === 0) {
    return (
      <div className={tableStyles.emptyState}>
        <p className={tableStyles.emptyTitle}>אין הצעות מחיר שנבחרו עדיין</p>
        <p className={tableStyles.emptyDescription}>בחר הצעות מחיר מדף הספקים כדי שיופיעו כאן</p>
      </div>
    );
  }

  return (
    <div>
      <div className={tableStyles.tableContainer}>
        <table className={tableStyles.table}>
          <thead>
            <tr className={tableStyles.headerRow}>
              <th className={tableStyles.headerCell}>סוג עבודה</th>
              <th className={tableStyles.headerCell}>ספק</th>
              <th className={tableStyles.headerCell}>סה״כ כולל מע״מ</th>
            </tr>
          </thead>
          <tbody>
            {groupedQuotes.map(group => (
              <React.Fragment key={group.categoryKey}>
                {/* Category header row */}
                <tr className={tableStyles.categoryHeader}>
                  <td colSpan={2}>{group.categoryName}</td>
                  <td>{formatCurrency(group.subtotal)}</td>
                </tr>

                {/* Quote rows within this category */}
                {group.rows.map(row => (
                  <tr
                    key={row.quote.id}
                    className={`${tableStyles.row} ${styles.clickableRow}`}
                    onClick={() =>
                      navigate(VENDOR_TYPE_ROUTES[row.tradeName] || DEFAULT_QUOTE_ROUTE)
                    }
                    title="לצפייה בפרטי ההצעה"
                  >
                    <td className={tableStyles.cell}>{row.tradeName}</td>
                    <td className={`${tableStyles.cell} ${tableStyles.cellPrimary}`}>
                      {row.vendorName}
                    </td>
                    <td className={tableStyles.cell}>
                      {row.total > 0 ? formatCurrency(row.total) : '—'}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}

            {/* Grand total row */}
            <tr className={styles.totalRow}>
              <td className={tableStyles.cell} colSpan={2}>
                סה״כ תקציב פעיל
              </td>
              <td className={tableStyles.cell}>{formatCurrency(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActiveBudgetTab;
